import { useState, useEffect, FormEvent } from "react";
import { useAdminAuth } from "@/lib/adminAuth";
import { toast } from "@/hooks/use-toast";

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export default function BlunaTab() {
  const { adminFetch } = useAdminAuth();
  const [prompt, setPrompt] = useState("");
  const [original, setOriginal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    adminFetch("api/admin/settings/prompt")
      .then(async (r) => {
        if (!r.ok) throw new Error(`Failed to load prompt (${r.status})`);
        return r.json();
      })
      .then((data) => {
        setPrompt(data.prompt ?? "");
        setOriginal(data.prompt ?? "");
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  }, [adminFetch]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await adminFetch("api/admin/settings/prompt", {
        method: "PUT",
        body: JSON.stringify({ prompt }),
      });
      if (!res.ok) throw new Error("Save failed");
      setOriginal(prompt);
      toast({ title: "Saved", description: "Bluna's system prompt has been updated." });
    } catch (e: unknown) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const isDirty = prompt !== original;

  if (loading) return <div className="text-white/50 py-8 text-center">Loading prompt…</div>;

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white">Bluna System Prompt</h2>
        <p className="text-white/40 text-sm mt-0.5">This controls how Bluna behaves in the chat — including matching logic, catalogue, and tone.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm font-mono focus:outline-none focus:border-purple-500 resize-y transition"
          rows={28}
          placeholder="Enter Bluna's system prompt…"
          required
          spellCheck={false}
        />
        {error && <p className="text-red-400 text-sm">{error}</p>}
        <div className="flex gap-3">
          <button
            type="button"
            disabled={!isDirty}
            onClick={() => setPrompt(original)}
            className="border border-white/20 text-white/60 px-4 py-2 rounded-lg hover:bg-white/5 disabled:opacity-30 transition text-sm"
          >
            Discard changes
          </button>
          <button
            type="submit"
            disabled={saving || !isDirty}
            className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white px-6 py-2 rounded-lg transition text-sm font-medium"
          >
            {saving ? "Saving…" : "Save Prompt"}
          </button>
        </div>
      </form>
    </div>
  );
}
